import { useMemo } from "react";

import type { ChartTableSelection } from "../../lib/chartTables.js";
import { SHARED_FULL_WEEKS_CRITERIA, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import { selectedDayUptime, sumDeviceDayCount } from "../../lib/chartTableSeries.js";
import {
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type { FilterState, RetentionData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

/** Same purple as Payment Flow Funnel Interaction Stints. */
const STINT_FILL = "#8b7cf6";

/** Above this, % labels on the bar tops overlap and are omitted. */
const VALUE_LABEL_MAX_BARS = 7;

type DayRow = RetentionData["stintsToPaymentConfirmedByDay"][number];

function formatPct(ratio: number): string {
  const pct = ratio * 100;
  if (pct > 0 && pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

function formatAxisTick(percentPoints: number): string {
  if (percentPoints > 0 && percentPoints < 10) return `${percentPoints.toFixed(1)}%`;
  return `${Math.round(percentPoints)}%`;
}

function weekTotals(
  week: UptimeWeekBucket,
  byDay: ReadonlyMap<string, DayRow>,
): { stintCount: number; paymentConfirmedCount: number; pct: number | null } {
  let stintCount = 0;
  let paymentConfirmedCount = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(week.monday, i);
    const row = day ? byDay.get(day) : undefined;
    stintCount += row?.stintCount ?? 0;
    paymentConfirmedCount += row?.paymentConfirmedCount ?? 0;
  }
  return {
    stintCount,
    paymentConfirmedCount,
    pct: stintCount > 0 ? paymentConfirmedCount / stintCount : null,
  };
}

const CRITERIA = `Bar = **payment confirmed ÷ interaction stints**, as a percent.

**Denominator:** distinct non-empty **interaction_session_id** whose first event in the window falls on that reporting day. Same grain and filters as Payment Flow Funnel **Interaction Stints** (dashboard filters including Game).

**Numerator:** play-session grain, same inclusion as the funnel **Payment Confirmed** bar / Paid session explorer (registered table unlocks plus shell-gap confirms). Bucketed by the reporting day of **payment_confirmed** (else the first pay event on that play session id). Date + venue + device only — **no Game**.

The two series are counted independently, so a day can exceed 100% (a stint can pay the next day, or one stint can have more than one paid session). Null (**—**) when that day has no stints.

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\`): one bar per complete **Monday–Sunday** whose seven days are inside the window and each have a full day's capacity (same rule as Revenue by day / Health Uptime by day). Percent is **summed confirms ÷ summed stints**, not an average of daily percents. Partial weeks are omitted.

Value labels above bars are omitted when there are more than 7 days or weeks. Tooltip always includes the **%**. Composite metric — no Raw Events drill-down.

${SHARED_TABLES_CRITERIA}

${SHARED_FULL_WEEKS_CRITERIA}`;

export function StintsToPaymentConfirmedCard({
  rows: fleetRows,
  tableCount: fleetTableCount,
  devices,
  uptimeByDeviceDay,
  stintsByDeviceDay,
  paymentConfirmedByDeviceDay,
  chartTables,
  onChartTablesChange,
  fullWeeks,
  onFullWeeksChange,
  filters,
}: {
  rows: DayRow[];
  tableCount: number;
  devices: RetentionData["chartDevices"];
  uptimeByDeviceDay: RetentionData["uptimeByDeviceDay"];
  stintsByDeviceDay: RetentionData["stintsByDeviceDay"];
  paymentConfirmedByDeviceDay: RetentionData["paymentConfirmedByDeviceDay"];
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  fullWeeks: boolean;
  onFullWeeksChange: (next: boolean) => void;
  filters: FilterState;
}) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);
  const stintCounts = useMemo(
    () => stintsByDeviceDay.map((row) => ({ ...row, count: row.stintCount })),
    [stintsByDeviceDay],
  );
  const confirmCounts = useMemo(
    () =>
      paymentConfirmedByDeviceDay.map((row) => ({
        ...row,
        count: row.paymentConfirmedCount,
      })),
    [paymentConfirmedByDeviceDay],
  );
  const rows = useMemo(() => {
    if (tables.useFleetSeries) return fleetRows;
    if (tables.effectiveIds.size === 0) return [];
    return fleetRows.map((row) => {
      const up = selectedDayUptime(uptimeByDeviceDay, tables.effectiveIds, row.day);
      const stintCount = sumDeviceDayCount(stintCounts, tables.effectiveIds, row.day);
      const paymentConfirmedCount = sumDeviceDayCount(confirmCounts, tables.effectiveIds, row.day);
      return {
        day: row.day,
        stintCount,
        paymentConfirmedCount,
        pct: stintCount > 0 ? paymentConfirmedCount / stintCount : null,
        capacityHours: up.capacityHours,
      };
    });
  }, [
    confirmCounts,
    fleetRows,
    stintCounts,
    tables.effectiveIds,
    tables.useFleetSeries,
    uptimeByDeviceDay,
  ]);
  const tableCount = tables.useFleetSeries ? fleetTableCount : tables.effectiveIds.size;
  const byDay = useMemo(() => new Map(rows.map((row) => [row.day, row])), [rows]);

  const weeks = useMemo(() => {
    if (!fullWeeks || tableCount <= 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        day: row.day,
        uptimeHours: 0,
        scheduledHours: 0,
        capacityHours: row.capacityHours,
        unmeasuredHours: 0,
        expectedHours: 0,
      })),
      tableCount,
    );
  }, [fullWeeks, rows, tableCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const { pct } = weekTotals(week, byDay);
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: pct == null ? 0 : pct * 100,
          valueLabel: pct == null ? "—" : formatPct(pct),
          fill: STINT_FILL,
        };
      });
    }
    return rows.map((row) => ({
      ...dailyChartBarAxisProps(row.day),
      value: row.pct == null ? 0 : row.pct * 100,
      valueLabel: row.pct == null ? "—" : formatPct(row.pct),
      fill: STINT_FILL,
    }));
  }, [byDay, fullWeeks, rows, weeks]);

  const shownStints = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).stintCount, 0)
    : rows.reduce((sum, row) => sum + row.stintCount, 0);
  const shownConfirmed = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).paymentConfirmedCount, 0)
    : rows.reduce((sum, row) => sum + row.paymentConfirmedCount, 0);
  const overallPct = shownStints > 0 ? shownConfirmed / shownStints : null;
  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  return (
    <ChartCard
      compact
      title="Stints to payment confirmed"
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
            <input
              type="checkbox"
              className="accent-accent-2"
              checked={fullWeeks}
              onChange={(e) => onFullWeeksChange(e.target.checked)}
            />
            Full weeks
          </label>
          <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
            {overallPct == null ? "—" : formatPct(overallPct)} overall
          </span>
        </div>
      }
      criteria={CRITERIA}
      note={
        fullWeeks
          ? "One bar per complete Monday–Sunday. Percent is summed confirms ÷ summed stints. Partial weeks (including this week if it has not finished) are omitted."
          : "Payment confirmed (play-session grain) ÷ interaction stints that started that day. Purple matches Interaction Stints. Can exceed 100%."
      }
    >
      <div className="mb-1 flex justify-end">
        <ChartTablePicker
          devices={devices}
          selectedIds={tables.effectiveIds}
          onSelectedIdsChange={tables.onSelectedIdsChange}
          locked={tables.filterLocksSingle}
        />
      </div>
      {!tables.useFleetSeries && tables.effectiveIds.size === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : fullWeeks && bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {FULL_WEEKS_EMPTY_MESSAGE}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          showYAxis
          showValueLabels={showValueLabels}
          formatYAxisTick={formatAxisTick}
          barTooltip={(item, index) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const totals = weekTotals(week, byDay);
              const pctBit = totals.pct == null ? "—" : formatPct(totals.pct);
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ${pctBit} (${totals.paymentConfirmedCount.toLocaleString()} payment confirmed ÷ ${totals.stintCount.toLocaleString()} interaction stints). Summed counts, not an average of daily percents. No Raw Events drill-down from this chart.`;
            }
            const row = rows[index];
            if (!row) return item.label;
            const pctBit = row.pct == null ? "—" : formatPct(row.pct);
            return `${formatDashboardDayLabelLong(row.day)}: ${pctBit} (${row.paymentConfirmedCount.toLocaleString()} payment confirmed ÷ ${row.stintCount.toLocaleString()} interaction stints). No Raw Events drill-down from this chart.`;
          }}
        />
      )}
    </ChartCard>
  );
}
